import { cabinetWorldBox, partBox, type EvaluatedCabinet, type PanelPart, type ProjectDocument, type Vec3 } from '@scs/domain-engine';

/** Axis-aligned world box in µm. */
export interface WBox {
  min: Vec3;
  max: Vec3;
}

export interface SnapTarget {
  box: WBox;
  label: string;
}

export interface SnapPlane {
  axis: 0 | 1 | 2;
  value: number;
  /** Which face of the moving box may land on the plane. */
  side: 'min' | 'max';
  label: string;
}

export interface SnapHit {
  axis: 0 | 1 | 2;
  kind: 'touch' | 'align' | 'center' | 'plane';
  label: string;
  /** World coordinate (µm) of the snapped face/line, for drawing a guide. */
  at: number;
}

export interface SnapResult {
  delta: Vec3;
  hits: SnapHit[];
}

export const SNAP_THRESHOLD = 40_000; // 40 mm
/** Max gap on the other two axes for a neighbour to count as "next to" the moving object. */
export const NEIGHBOUR_GAP = 300_000; // 300 mm

const AXES = [0, 1, 2] as const;
const AXIS_NAME = ['X', 'Y', 'Z'] as const;

function gapOnAxis(a: WBox, b: WBox, k: 0 | 1 | 2): number {
  return Math.max(0, a.min[k] - b.max[k], b.min[k] - a.max[k]);
}

export function translateBox(b: WBox, d: Vec3): WBox {
  return { min: [b.min[0] + d[0], b.min[1] + d[1], b.min[2] + d[2]], max: [b.max[0] + d[0], b.max[1] + d[1], b.max[2] + d[2]] };
}

/**
 * Snaps a move. For each axis independently the smallest correction within `threshold` wins, among:
 *  - touch: moving face against the opposite face of a neighbour (side by side, on top, behind),
 *  - align: same-side faces flush (fronts, backs, tops, bottoms, ends) and centres,
 *  - planes: room walls and floor.
 * Neighbours only count when they are within NEIGHBOUR_GAP on the other two axes, so far objects never pull.
 */
export function snapMove(moving: WBox, rawDelta: Vec3, targets: SnapTarget[], planes: SnapPlane[], axes: readonly boolean[] = [true, true, true], threshold = SNAP_THRESHOLD): SnapResult {
  const moved = translateBox(moving, rawDelta);
  const delta: Vec3 = [...rawDelta];
  const hits: SnapHit[] = [];
  for (const k of AXES) {
    if (!axes[k]) continue;
    let best: { adj: number; hit: SnapHit } | null = null;
    const consider = (adj: number, hit: SnapHit) => {
      if (Math.abs(adj) <= threshold && (!best || Math.abs(adj) < Math.abs(best.adj))) best = { adj, hit };
    };
    for (const t of targets) {
      const others = AXES.filter((x) => x !== k);
      if (others.some((o) => gapOnAxis(moved, t.box, o) > NEIGHBOUR_GAP)) continue;
      const name = AXIS_NAME[k];
      consider(t.box.min[k] - moved.max[k], { axis: k, kind: 'touch', label: `${name}: uz ${t.label}`, at: t.box.min[k] });
      consider(t.box.max[k] - moved.min[k], { axis: k, kind: 'touch', label: `${name}: uz ${t.label}`, at: t.box.max[k] });
      consider(t.box.min[k] - moved.min[k], { axis: k, kind: 'align', label: `${name}: poravnato sa ${t.label}`, at: t.box.min[k] });
      consider(t.box.max[k] - moved.max[k], { axis: k, kind: 'align', label: `${name}: poravnato sa ${t.label}`, at: t.box.max[k] });
      const tc = (t.box.min[k] + t.box.max[k]) / 2;
      const mc = (moved.min[k] + moved.max[k]) / 2;
      consider(Math.round(tc - mc), { axis: k, kind: 'center', label: `${name}: centrirano na ${t.label}`, at: tc });
    }
    for (const p of planes) {
      if (p.axis !== k) continue;
      consider(p.value - (p.side === 'min' ? moved.min[k] : moved.max[k]), { axis: k, kind: 'plane', label: `${AXIS_NAME[k]}: ${p.label}`, at: p.value });
    }
    if (best) {
      const b = best as { adj: number; hit: SnapHit };
      delta[k] += b.adj;
      hits.push(b.hit);
    }
  }
  return { delta: delta.map((v) => Math.round(v)) as Vec3, hits };
}

/** Room planes: floor, left/right walls, back wall. */
export function roomPlanes(doc: ProjectDocument): SnapPlane[] {
  return [
    { axis: 1, value: 0, side: 'min', label: 'pod' },
    { axis: 0, value: 0, side: 'min', label: 'lijevi zid' },
    { axis: 0, value: doc.room.width, side: 'max', label: 'desni zid' },
    { axis: 2, value: 0, side: 'min', label: 'zadnji zid' },
  ];
}

// ---------- world transforms (quarter turns + mirror), µm ----------

/** Local box of a unit → world box (same convention as the viewer and cabinetWorldBox). */
export function localToWorldBox(c: EvaluatedCabinet, min: Vec3, size: Vec3): WBox {
  const w = c.result.bounds.size[0];
  const xs = c.instance.transform.mirrored ? [w - (min[0] + size[0]), w - min[0]] : [min[0], min[0] + size[0]];
  const phi = (-c.instance.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  const [px, py, pz] = c.instance.transform.position;
  const wx: number[] = [];
  const wz: number[] = [];
  for (const x of xs) {
    for (const z of [min[2], min[2] + size[2]]) {
      wx.push(px + x * cos + z * sin);
      wz.push(pz - x * sin + z * cos);
    }
  }
  return { min: [Math.min(...wx), py + min[1], Math.min(...wz)], max: [Math.max(...wx), py + min[1] + size[1], Math.max(...wz)] };
}

export function partWorldBox(c: EvaluatedCabinet, part: PanelPart): WBox {
  const b = partBox(part);
  return localToWorldBox(c, b.min, b.size);
}

export function unitWorldBox(c: EvaluatedCabinet): WBox {
  return cabinetWorldBox(c);
}

/** World-space move of a panel → change of its assembly-local position (inverse rotation, then mirror). */
export function worldDeltaToLocal(c: EvaluatedCabinet, d: Vec3): Vec3 {
  const phi = (-c.instance.transform.rotationY * Math.PI) / 180;
  const cos = Math.round(Math.cos(phi));
  const sin = Math.round(Math.sin(phi));
  let x = d[0] * cos - d[2] * sin;
  const z = d[0] * sin + d[2] * cos;
  if (c.instance.transform.mirrored) x = -x;
  return [Math.round(x), d[1], Math.round(z)];
}
